import React, { useState } from 'react'
import { PREDEFINED_STAGES, PREDEFINED_TASKS, PREDEFINED_SUBTASKS } from '../../common/task-stages'
import { DEPENDENCY_TYPES } from '../../common/task-constants'

interface Dependency {
  dependencyType: string
  stageName: string
  customStageName: string
  customTaskName: string
  customTaskText: string
  customSubtaskName: string
  customSubtaskText: string
}

interface AddDependencyModalProps {
  onClose: () => void
  onAddDependency: (dependencyData: Dependency) => Promise<void>
  onUpdateDependency: (dependencyId: string, dependencyData: Dependency) => Promise<void>
  onRemoveDependency: (dependencyId: string) => Promise<void>
  existingDependencies: Array<Dependency & { id: string }>
}

// Funkcja do pobierania podzadań dla danego etapu i zadania
const getSubtasksForTask = (stageName: string, taskName: string): string[] => {
  return PREDEFINED_SUBTASKS[stageName]?.[taskName] || []
}

export const AddDependencyModal: React.FC<AddDependencyModalProps> = ({
  onClose,
  onAddDependency,
  onUpdateDependency,
  onRemoveDependency,
  existingDependencies
}) => {
  const [dependencies, setDependencies] = useState<Array<Dependency & { id: string }>>(() => {
    // Tworzymy 3 powiązania, każde z innym typem
    const initialDependencies = DEPENDENCY_TYPES.map(type => {
      // Szukamy istniejącego powiązania dla danego typu
      const existingDep = existingDependencies.find(dep => dep.dependencyType === type.value)

      // Sprawdzamy czy etap jest customowy
      const isCustomStage = (existingDep != null) && !PREDEFINED_STAGES.includes(existingDep.stageName)

      // Sprawdzamy czy zadanie jest customowe
      const isCustomTask = (existingDep != null) && (!PREDEFINED_TASKS[existingDep.stageName] ||
        !PREDEFINED_TASKS[existingDep.stageName].includes(existingDep.customTaskName))

      // Sprawdzamy czy podzadanie jest customowe
      const isCustomSubtask = (existingDep != null) && existingDep.customSubtaskName && (!getSubtasksForTask(existingDep.stageName, existingDep.customTaskName).includes(existingDep.customSubtaskName))

      return {
        id: existingDep?.id || '',
        dependencyType: type.value,
        stageName: isCustomStage ? 'custom' : (existingDep?.stageName || ''),
        customStageName: isCustomStage ? existingDep?.stageName || '' : '',
        customTaskName: isCustomTask ? 'custom' : (existingDep?.customTaskName || ''),
        customTaskText: isCustomTask ? existingDep?.customTaskName || '' : '',
        customSubtaskName: isCustomSubtask ? 'custom' : (existingDep?.customSubtaskName || ''),
        customSubtaskText: isCustomSubtask ? existingDep?.customSubtaskName || '' : ''
      }
    })

    return initialDependencies
  })

  const getTasksForStage = (stageName: string) => {
    return PREDEFINED_TASKS[stageName] || []
  }

  const handleAddClick = async () => {
    for (const dependency of dependencies) {
      if (dependency.stageName) {
        const stageName = dependency.stageName === 'custom'
          ? dependency.customStageName
          : dependency.stageName

        const customTaskName = dependency.stageName === 'custom'
          ? dependency.customTaskText
          : (dependency.customTaskName === 'custom'
              ? dependency.customTaskText
              : dependency.customTaskName)

        const customSubtaskName = dependency.customSubtaskName === 'custom'
          ? dependency.customSubtaskText
          : dependency.customSubtaskName || ''

        if (stageName && customTaskName) {
          if (dependency.id) {
            await onUpdateDependency(dependency.id, {
              dependencyType: dependency.dependencyType,
              stageName,
              customTaskName,
              customSubtaskName,
              customSubtaskText: '',
              customStageName: '',
              customTaskText: ''
            })
          } else {
            await onAddDependency({
              dependencyType: dependency.dependencyType,
              stageName,
              customTaskName,
              customSubtaskName,
              customSubtaskText: '',
              customStageName: '',
              customTaskText: ''
            })
          }
        }
      }
    }
    onClose()
  }

  const handleRemoveDependency = async (index: number) => {
    const dependency = dependencies[index]
    if (dependency.id) {
      try {
        await onRemoveDependency(dependency.id)
        // Resetujemy powiązanie po pomyślnym usunięciu
        setDependencies(prev => {
          const newDependencies = [...prev]
          newDependencies[index] = {
            ...newDependencies[index],
            id: '',
            stageName: '',
            customStageName: '',
            customTaskName: '',
            customTaskText: '',
            customSubtaskName: '',
            customSubtaskText: ''
          }
          return newDependencies
        })
      } catch (error) {
        console.error('Błąd podczas usuwania powiązania:', error)
      }
    } else {
      // Jeśli nie ma ID, po prostu resetujemy wartości
      setDependencies(prev => {
        const newDependencies = [...prev]
        newDependencies[index] = {
          ...newDependencies[index],
          stageName: '',
          customStageName: '',
          customTaskName: '',
          customTaskText: '',
          customSubtaskName: '',
          customSubtaskText: ''
        }
        return newDependencies
      })
    }
  }

  const updateDependency = (index: number, field: string, value: string) => {
    setDependencies(prevDependencies => {
      const newDependencies = [...prevDependencies]
      newDependencies[index] = {
        ...newDependencies[index],
        [field]: value
      }
      return newDependencies
    })
  }

  const isAddButtonDisabled = false // Przycisk zawsze aktywny

  return (
    <div className='modal fade show' style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1051 }}>
      <div className='modal-dialog modal-lg' style={{ maxHeight: '90vh', margin: '5vh auto' }}>
        <div className='modal-content' style={{ maxHeight: '90vh' }}>
          <div className='modal-header'>
            <h5 className='modal-title'>Edytuj powiązania</h5>
            <button type='button' className='close' onClick={onClose}>
              <span>&times;</span>
            </button>
          </div>
          <div className='modal-body' style={{ overflowY: 'auto', maxHeight: 'calc(90vh - 120px)' }}>
            {dependencies.map((dependency, index) => (
              <div key={index} className='mb-4 p-3 border rounded position-relative'>
                <button
                  type='button'
                  className={`
                      tw-absolute tw-top-2.5 tw-right-2.5 tw-text-center tw-font-semibold tw-rounded-md tw-shadow-sm tw-transition-colors tw-text-white
                      tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base tw-bg-red-500 hover:tw-bg-opacity-90 focus:tw-bg-red-500
                    `}
                  onClick={async () => await handleRemoveDependency(index)}
                >
                  Wyczyść
                </button>
                <h6 className='mb-3 tw-font-semibold'>
                  {DEPENDENCY_TYPES.find(type => type.value === dependency.dependencyType)?.label}
                </h6>
                {/* Etap */}
                <div className='form-group mb-3'>
                  <label>Kategoria:</label>
                  <select
                    className='form-control'
                    value={dependency.stageName}
                    onChange={(e) => {
                      const newStageName = e.target.value
                      updateDependency(index, 'stageName', newStageName)
                      updateDependency(index, 'customTaskName', '')
                      updateDependency(index, 'customTaskText', '')
                      updateDependency(index, 'customSubtaskName', '')
                      updateDependency(index, 'customSubtaskText', '')
                      if (newStageName !== 'custom') {
                        updateDependency(index, 'customStageName', '')
                      }
                    }}
                  >
                    <option value=''>Wybierz kategorię lub wprowadź własną</option>
                    {PREDEFINED_STAGES.map(stage => (
                      <option key={stage} value={stage}>{stage}</option>
                    ))}
                    <option value='custom'>Własna kategoria...</option>
                  </select>
                </div>

                {/* Własny etap */}
                {dependency.stageName === 'custom' && (
                  <div className='form-group mb-3'>
                    <label>Nazwa kategorii:</label>
                    <input
                      type='text'
                      className='form-control'
                      placeholder='Wprowadź nazwę kategorii'
                      value={dependency.customStageName}
                      onChange={(e) => updateDependency(index, 'customStageName', e.target.value)}
                    />
                  </div>
                )}

                {/* Zadanie */}
                <div className='form-group mb-3'>
                  <label>Podkategoria:</label>
                  {dependency.stageName && dependency.stageName !== 'custom'
                    ? (
                      <select
                        className='form-control'
                        value={dependency.customTaskName}
                        onChange={(e) => {
                          updateDependency(index, 'customTaskName', e.target.value)
                          updateDependency(index, 'customTaskText', '')
                          updateDependency(index, 'customSubtaskName', '')
                          updateDependency(index, 'customSubtaskText', '')
                        }}
                      >
                        <option value=''>Wybierz podkategorię lub wprowadź własną</option>
                        {getTasksForStage(dependency.stageName).map(task => (
                          <option key={task} value={task}>{task}</option>
                        ))}
                        <option value='custom'>Własna podkategoria...</option>
                      </select>
                      )
                    : (
                      <input
                        type='text'
                        className='form-control'
                        placeholder='Wprowadź nazwę podkategorii'
                        value={dependency.customTaskText}
                        onChange={(e) => updateDependency(index, 'customTaskText', e.target.value)}
                      />
                      )}

                  {dependency.stageName && dependency.stageName !== 'custom' && dependency.customTaskName === 'custom' && (
                    <input
                      type='text'
                      className='form-control mt-2'
                      placeholder='Wprowadź nazwę podkategorii'
                      value={dependency.customTaskText}
                      onChange={(e) => updateDependency(index, 'customTaskText', e.target.value)}
                    />
                  )}
                </div>

                {/* Podzadanie */}
                <div className='form-group mb-3'>
                  <label>Etap:</label>
                  {dependency.stageName && dependency.stageName !== 'custom' && dependency.customTaskName && dependency.customTaskName !== 'custom'
                    ? (
                      <select
                        className='form-control'
                        value={dependency.customSubtaskName || ''}
                        onChange={(e) => {
                          updateDependency(index, 'customSubtaskName', e.target.value)
                          updateDependency(index, 'customSubtaskText', '')
                        }}
                      >
                        <option value=''>Wybierz etap lub wprowadź własny</option>
                        {getSubtasksForTask(dependency.stageName, dependency.customTaskName).map(subtask => (
                          <option key={subtask} value={subtask}>{subtask}</option>
                        ))}
                        <option value='custom'>Własny etap...</option>
                      </select>
                      )
                    : (
                      <input
                        type='text'
                        className='form-control'
                        placeholder='Wprowadź nazwę etapu'
                        value={dependency.customSubtaskText || ''}
                        onChange={(e) => updateDependency(index, 'customSubtaskText', e.target.value)}
                      />
                      )}
                  {dependency.stageName && dependency.stageName !== 'custom' && dependency.customTaskName && dependency.customTaskName !== 'custom' && dependency.customSubtaskName === 'custom' && (
                    <input
                      type='text'
                      className='form-control mt-2'
                      placeholder='Wprowadź nazwę podzadania'
                      value={dependency.customSubtaskText || ''}
                      onChange={(e) => updateDependency(index, 'customSubtaskText', e.target.value)}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className='modal-footer'>
            <button
              type='button'
              className={`
                  tw-text-center tw-font-semibold tw-rounded-md tw-shadow-sm tw-transition-colors tw-text-white
                  tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base tw-bg-dark-teal hover:tw-bg-dark-teal/80 focus:tw-bg-dark-teal
                `}
              onClick={onClose}
            >
              Anuluj
            </button>

            <button
              type='button'
              className={`
                  tw-mr-2 tw-text-center tw-font-semibold tw-rounded-md tw-shadow-sm tw-transition-colors tw-text-white
                  tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base tw-bg-green hover:tw-bg-opacity-90 focus:tw-bg-green
                `}
              onClick={handleAddClick}
              disabled={isAddButtonDisabled}
            >
              Zapisz zmiany
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
